import { getJson } from '$lib/api/fetch';
import { UnauthorizedError } from '$lib/api/types';
import { startImpersonation, stopImpersonation } from '$lib/api/users';
import { userRoleRedirect } from '$lib/util';
import { toast } from './toast.svelte';

interface User {
  environment: string;
  name: string;
  role: string;
  acceptedResearchConsent?: boolean;
  redirectClassId?: number;
  userId: number; // id of the user record in the user table
  /**
   * The ID of the user impersonating the logged in user. Undefined if not impersonating.
   */
  impersonatorId?: string;
}

class AuthStore {
  environment = $state<null | string>(null);
  user = $state<User | null>(null);
  isAdmin = $derived(this.user?.role.includes('admin') ?? false);
  loading = $state<boolean>(true);
  signInUrl = $state<string | null>(null);
  /**
   * Ping the backend for user status.
   * @param initial Adds `initial=true` to the request to include a class ID if user is part of a single class.
   */
  async ping(initial: boolean) {
    const wasLoggedIn = this.user !== null;
    try {
      this.user = await getJson<User>(`/auth/ping?${initial ? 'initial=true' : ''}`);
      this.environment = this.user.environment;
      this.loading = false;
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        type WithEnvironment = { environment?: string };
        console.log('checking for environment...');
        if (e.body && (e.body as WithEnvironment).environment) {
          console.log('found environment!');
          this.environment = (e.body as WithEnvironment).environment ?? null;
        }
        this.user = null;
        this.loading = false;
        if (wasLoggedIn) {
          toast.showToast('danger', 'You have been logged out.');
        }
      } else {
        throw e;
      }
    }
  }

  acceptResearchConsent() {
    if (!this.user) {
      throw new Error('acceptResearchConsent called before auth.user initialized');
    }
    this.user = {
      ...this.user,
      acceptedResearchConsent: true,
    };
  }

  canImpersonate(id: number) {
    return id !== this.user?.userId && id.toString() !== this.user?.impersonatorId;
  }

  async startAuthImpersonation(userId: number) {
    this.loading = true;
    await startImpersonation(userId);
    await this.ping(true);
    toast.showToast('success', 'Impersonation has started.');
    userRoleRedirect();
  }

  async stopAuthImpersonation() {
    this.loading = true;
    await stopImpersonation();
    await this.ping(true);
    toast.showToast('success', 'Impersonation has stopped.');
    userRoleRedirect();
  }
}

export const auth = new AuthStore();
